import React, { useEffect, useState } from 'react';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';

// -- Delete or archive a ticket (18 Oct) --------------------------------------
//
// Lightspeed decides which: an empty ticket is deleted, one with items is
// archived (server/helpers/removeTicket.js). There is no undo in Lightspeed's
// API, so this dialog asks the server first and says exactly what will happen,
// what stops it, and what happens to anything Spinboard has linked to it.

// Who sees the button. remove_ticket arrived 18 Oct; anyone still signed in
// from before has a stored user without it, so fall back to the role the
// server's defaults give it to. The server checks the permission regardless.
export function canRemoveTickets(user) {
  const p = user?.permissions?.remove_ticket;
  if (p === true || p === false) return p;
  return ['owner', 'manager'].includes(user?.role);
}

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function RemoveTicket({ T, isDark, ticketId, onClose, onRemoved }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/api/tickets/${ticketId}/remove-preview`, { credentials: 'include', headers: authHeaders() })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || 'Could not check this ticket.'); return d; })
      .then(d => { if (live) setPreview(d); })
      .catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [ticketId]);

  const go = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/tickets/${ticketId}/remove`, { method: 'POST', credentials: 'include', headers: authHeaders(true), body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Lightspeed did not remove the ticket.');
      onRemoved(d);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const archive = preview?.mode === 'archive';
  const blocked = preview && preview.blockers.length > 0;
  const verb = archive ? 'Archive' : 'Delete';
  const red = '#e74c3c';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={() => !busy && onClose()}>
      <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 480, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 10 }}>
          {preview ? `${verb} ticket #${ticketId}?` : `Remove ticket #${ticketId}`}
        </div>

        {!preview && !error && <div style={{ fontSize: 13, color: T.textSub }}>Checking the ticket in Lightspeed…</div>}

        {preview && blocked && (
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
            {preview.blockers.map(b => <p key={b.code} style={{ margin: '0 0 10px' }}>{b.message}</p>)}
          </div>
        )}

        {preview && !blocked && (
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55 }}>
            <p style={{ margin: '0 0 10px' }}>
              {archive
                ? <>This ticket has {preview.itemCount} item{preview.itemCount === 1 ? '' : 's'} on it, so Lightspeed will <b>archive</b> it rather than delete it. It leaves the queue and every Spinboard screen, and stays in Lightspeed's archived work orders.</>
                : <>This ticket is empty, so Lightspeed will <b>delete</b> it for good.</>}
            </p>
            <p style={{ margin: '0 0 10px', color: red }}><b>This cannot be undone from Spinboard.</b></p>
            {preview.onAccount > 0 && (
              <p style={{ margin: '0 0 10px' }}>
                The customer paid <b>{fmt(preview.onAccount)}</b> toward this ticket. That money is on their Lightspeed account, not the ticket, so it <b>stays there</b> to use at the register or refund from Payments.
              </p>
            )}
            {preview.openLinks.length > 0 && (
              <p style={{ margin: '0 0 10px' }}>
                {preview.openLinks.length === 1 ? 'An unpaid payment link' : `${preview.openLinks.length} unpaid payment links`} for this ticket ({preview.openLinks.map(l => fmt(l.amount)).join(', ')}) will be cancelled, so the customer can't pay for it.
              </p>
            )}
            <p style={{ margin: 0, color: T.textSub, fontSize: 12 }}>Texts with this customer are kept, just no longer linked to the ticket. Time and checklists recorded on it stay in the effort history.</p>
          </div>
        )}

        {error && <div role="alert" style={{ fontSize: 13, color: red, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" disabled={busy} onClick={onClose}
            style={{ ...S.btn(false), color: T.textSub, borderColor: T.border }}>{blocked || error ? 'Close' : 'Keep it'}</button>
          {preview && !blocked && (
            <button type="button" disabled={busy} onClick={go}
              style={{ ...S.btn(true), background: red, color: '#fff', opacity: busy ? 0.6 : 1 }}>
              {busy ? `${verb === 'Archive' ? 'Archiving' : 'Deleting'}…` : `${verb} ticket`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
